// Imports
import { PostOrPage, Pagination } from '@tryghost/content-api';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { categoryMapping } from '..';
import useUpdateOnlyEffect from '../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import ghost from '../../../../shareables/integrations/ghost';
import LoadMoreButton from './load-more-button';
import Preview from './preview';
import prerenderData from '../../../../utils/prerender-data';


// Define what the prerendered previews snapshot looks like
interface PrerenderedPreviews {
	posts: PostOrPage[];
	pagination: Pagination;
}


// The number of posts shown per page
//  -> Keep synced with the blog hydration worker
const postsPerPage = 4;


// Styled components
const PostWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	column-gap: 1.25rem;
	row-gap: 2.5rem;
	justify-content: center;
	margin-top: 3.75rem;
	width: min(50rem, 95%);
`;


// Function that generates the filter string
const generateFilterString = (
	tagFilter: (typeof categoryMapping)[keyof typeof categoryMapping],
	postIDFilter?: string
) => {
	// Initialize
	const tagFilterString = tagFilter ? `[${tagFilter.join()}]` : '';
	
	
	// Return
	return `${tagFilterString ? `tag:${tagFilterString}` : ''}${postIDFilter ? `${tagFilterString ? '+' : ''}id:-${postIDFilter}` : ''}`;
};


// Function that generates the key identifying a set of previews within the prerendered snapshot
const generatePrerenderKey = (
	tagFilter: (typeof categoryMapping)[keyof typeof categoryMapping],
	initialPage: number,
	postIDFilter?: string
) => `blog:previews:${generateFilterString(tagFilter, postIDFilter)}:${initialPage * postsPerPage}`;


/** Reads the number of posts recorded for a set of previews in the prerendered snapshot */
export const readPrerenderedPostCount = (
	tagFilter: (typeof categoryMapping)[keyof typeof categoryMapping],
	initialPage: number,
	postIDFilter?: string
): number | undefined =>
	prerenderData.read<PrerenderedPreviews>(generatePrerenderKey(tagFilter, initialPage, postIDFilter))?.pagination.total;


// Define the accepted props
interface Props {
	tagFilter: (typeof categoryMapping)[keyof typeof categoryMapping];
	handleError: () => void;
	postIDFilter?: string;
	preventLoad?: boolean;
	setCount?: (count: number) => void;
	storybookPosts?: PostOrPage[];
	storybookPagination?: Pagination;
	initialPage: number;
	resetKey: string;
}


// Function component
const Previews: React.FC<Props> = ({
	tagFilter,
	handleError,
	postIDFilter,
	preventLoad,
	setCount,
	storybookPosts,
	storybookPagination,
	initialPage,
	resetKey,
}) => {
	// Use ref
	const initialPageRef = useRef(initialPage);
	const discardRevalidationRef = useRef<() => void>();
	
	
	// Initialize
	const filter = generateFilterString(tagFilter, postIDFilter);
	const targetCount = initialPageRef.current * postsPerPage;
	const prerenderKey = generatePrerenderKey(tagFilter, initialPageRef.current, postIDFilter);
	
	
	// Use state
	const prerendered = storybookPosts ? undefined : prerenderData.read<PrerenderedPreviews>(prerenderKey);
	const [posts, setPosts] = useState<PostOrPage[] | undefined>(storybookPosts ?? prerendered?.posts);
	const [pagination, setPagination] = useState<Pagination | undefined>(storybookPagination ?? prerendered?.pagination);
	
	
	// Reset posts and pagination on tag filter change
	useUpdateOnlyEffect(() => {
		// Prevent resetting posts and pagination in Storybook
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK) {
			return;
		}
		
		
		// Reset posts and pagination
		setPosts(undefined);
		setPagination(undefined);
		initialPageRef.current = 1;
	}, [resetKey]);
	
	
	// Load posts on mount
	useEffect(() => {
		// Return early in Storybook or when loading should explicitly be prevented
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK || preventLoad) {
			return;
		}
		
		
		// Ghost API caps limit at 100 per request
		//  -> https://docs.ghost.org/changes#return-max-100-results-from-apis-removing-limit=all-support
		const ghostMaxLimit = 100;
		
		
		// Determine how many pages to fetch to cover the target count
		const fetchCount = Math.ceil(targetCount / ghostMaxLimit);
		const perRequestLimit = fetchCount === 1 ? targetCount : ghostMaxLimit;
		
		
		// Restore the prerendered posts then revalidate in the background
		discardRevalidationRef.current = prerenderData.revalidate<PrerenderedPreviews>(
			prerenderKey,
			(data) => {
				if (setCount) {
					setCount(data.pagination.total);
				}
				
				setPosts(data.posts);
				setPagination(data.pagination);
			},
			() => {
				const fetches = Array.from({ length: fetchCount }, (_, i) =>
					ghost.posts.browse({
						...(filter ? { filter } : {}),
						limit: perRequestLimit,
						page: i + 1,
						include: ['authors', 'tags'],
					})
				);
				
				return Promise.all(fetches).then((pages) => {
					const total = pages[0].meta.pagination.total;
					const pageCount = Math.ceil(total / postsPerPage);
					
					return {
						posts: pages.flat().slice(0, targetCount),
						pagination: {
							page: initialPageRef.current,
							limit: postsPerPage,
							pages: pageCount,
							total: total,
							next: initialPageRef.current < pageCount ? initialPageRef.current + 1 : null,
							prev: initialPageRef.current > 1 ? initialPageRef.current - 1 : null,
						},
					};
				});
			},
			() => {
				// Stay quiet while prerendering; an error notification shouldn't end up in the captured HTML
				if (!window.IS_PRERENDERING) {
					handleError();
				}
			}
		);
		
		return discardRevalidationRef.current;
	}, [filter, preventLoad, handleError, setCount, targetCount, prerenderKey]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PostWrapper>
				{posts
					? posts.map((post) => <Preview key={post.id} post={post} />)
					: Array.from({ length: targetCount }, (_, i) => <Preview key={i} />)}
			</PostWrapper>
			
			{pagination && (
				<LoadMoreButton
					update={(response) => {
						// Discard the revalidation so it can't overwrite the loaded posts
						discardRevalidationRef.current?.();
						
						if (setCount) {
							setCount(response.meta.pagination.total);
						}
						
						setPosts([...(posts ?? []), ...response]);
					}}
					initialPagination={pagination}
					filter={filter}
				/>
			)}
		</React.Fragment>
	);
};

export default Previews;
